/**
 * THE NAVIGATION REGISTRY.
 *
 * One list. The sidebar reads it to decide what to show, and the router reads
 * it to decide what to mount. That single source is the point:
 *
 *   - if a role lacks `translation.view`, "Bản dịch" does not appear in the menu
 *     AND `/translations` does not exist as a route, so typing the URL lands on
 *     the 403 screen instead of a half-rendered page firing forbidden requests;
 *   - a new screen cannot be added to the app without declaring who may see it.
 *
 * ===================================================================
 * HIDING A MENU ITEM IS NOT SECURITY.
 *
 * The permission list comes from `GET /api/v1/auth/me`, i.e. from the client's
 * own state, which a determined user can edit. It exists so the UI does not
 * offer actions that will fail. Real enforcement is server-side and
 * unconditional: every handler in apps/api-worker goes through
 * `requirePermission(...)` and reads through a `ScopedDb` that appends the
 * tenant/branch predicate to every query. Deleting this file would change what
 * is VISIBLE and change nothing about what is REACHABLE.
 * ===================================================================
 *
 * `permission: null` marks a screen every signed-in user may open (the profile
 * page). Everything else names a code from `@sonauto/permissions`.
 */
import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

export type NavGroup = 'dashboard' | 'operations' | 'catalog' | 'people' | 'finance' | 'system';

export const GROUP_LABEL_VI: Record<NavGroup, string> = {
  dashboard: 'Tổng quan',
  operations: 'Vận hành',
  catalog: 'Thực đơn & nội dung',
  people: 'Nhân sự & phân quyền',
  finance: 'Tài chính',
  system: 'Hệ thống',
};

export const GROUP_ORDER: readonly NavGroup[] = [
  'dashboard',
  'operations',
  'catalog',
  'people',
  'finance',
  'system',
];

export interface NavEntry {
  readonly id: string;
  /** Route path, relative to the app root. Must start with `/`. */
  readonly path: string;
  readonly titleVi: string;
  /** Emoji glyph — deliberate: zero bytes of icon font, renders everywhere. */
  readonly icon: string;
  /** Permission code required to SEE the menu item and to REACH the route. */
  readonly permission: string | null;
  readonly group: NavGroup;
  readonly element: LazyExoticComponent<ComponentType>;
  /** Hide from the sidebar but keep the route (detail pages). */
  readonly hidden?: boolean;
  /** Match the route as a prefix, for `/products/:id` under `/products`. */
  readonly childPaths?: readonly string[];
  readonly descriptionVi?: string;
}

/*
 * Code-split per screen. The Admin app is large and a manager on a phone should
 * not download the floor-plan editor to look at yesterday's revenue.
 */
const DashboardOverview = lazy(() => import('./routes/DashboardOverview.js'));
const DashboardRealtime = lazy(() => import('./routes/DashboardRealtime.js'));
const BranchesPage = lazy(() => import('./routes/BranchesPage.js'));
const AreasTablesPage = lazy(() => import('./routes/AreasTablesPage.js'));
const MenuCategoriesPage = lazy(() => import('./routes/MenuCategoriesPage.js'));
const ProductsPage = lazy(() => import('./routes/ProductsPage.js'));
const ProductEditorPage = lazy(() => import('./routes/ProductEditorPage.js'));
const CombosPage = lazy(() => import('./routes/CombosPage.js'));
const PromotionsPage = lazy(() => import('./routes/PromotionsPage.js'));
const TranslationsPage = lazy(() => import('./routes/TranslationsPage.js'));
const EmployeesPage = lazy(() => import('./routes/EmployeesPage.js'));
const RolesPage = lazy(() => import('./routes/RolesPage.js'));
const DevicesPage = lazy(() => import('./routes/DevicesPage.js'));
const OrdersPage = lazy(() => import('./routes/OrdersPage.js'));
const PaymentsPage = lazy(() => import('./routes/PaymentsPage.js'));
const CashPage = lazy(() => import('./routes/CashPage.js'));
const InventoryPage = lazy(() => import('./routes/InventoryPage.js'));
const AlertsPage = lazy(() => import('./routes/AlertsPage.js'));
const AuditLogPage = lazy(() => import('./routes/AuditLogPage.js'));
const SettingsPage = lazy(() => import('./routes/SettingsPage.js'));
const ProfilePage = lazy(() => import('./routes/ProfilePage.js'));

export const NAV_ENTRIES: readonly NavEntry[] = [
  // ---- dashboard ---------------------------------------------------------
  {
    id: 'overview',
    path: '/',
    titleVi: 'Tổng quan',
    icon: '📊',
    permission: 'report.revenue.view',
    group: 'dashboard',
    element: DashboardOverview,
    descriptionVi: 'Doanh thu, đơn hàng và so sánh giữa các chi nhánh.',
  },
  {
    id: 'realtime',
    path: '/realtime',
    titleVi: 'Thời gian thực',
    icon: '🛰️',
    permission: 'table.view',
    group: 'dashboard',
    element: DashboardRealtime,
    descriptionVi: 'Sơ đồ bàn và tải bếp theo thời gian thực.',
  },

  // ---- operations --------------------------------------------------------
  {
    id: 'branches',
    path: '/branches',
    titleVi: 'Chi nhánh',
    icon: '🏬',
    permission: 'branch.view',
    group: 'operations',
    element: BranchesPage,
  },
  {
    id: 'areas-tables',
    path: '/tables',
    titleVi: 'Khu vực & bàn',
    icon: '🪑',
    permission: 'table.view',
    group: 'operations',
    element: AreasTablesPage,
    descriptionVi: 'Sơ đồ mặt bằng kéo thả, khu vực và trạng thái bàn.',
  },
  {
    id: 'orders',
    path: '/orders',
    titleVi: 'Đơn hàng',
    icon: '🧾',
    permission: 'order.view',
    group: 'operations',
    element: OrdersPage,
  },
  {
    id: 'inventory',
    path: '/inventory',
    titleVi: 'Kho',
    icon: '📦',
    permission: 'inventory.view',
    group: 'operations',
    element: InventoryPage,
  },
  {
    id: 'alerts',
    path: '/alerts',
    titleVi: 'Cảnh báo',
    icon: '🚨',
    permission: 'alert.view',
    group: 'operations',
    element: AlertsPage,
  },

  // ---- catalog -----------------------------------------------------------
  {
    id: 'menu-categories',
    path: '/menu/categories',
    titleVi: 'Nhóm món',
    icon: '🗂️',
    permission: 'product.view',
    group: 'catalog',
    element: MenuCategoriesPage,
  },
  {
    id: 'products',
    path: '/products',
    titleVi: 'Sản phẩm',
    icon: '🍜',
    permission: 'product.view',
    group: 'catalog',
    element: ProductsPage,
  },
  {
    id: 'product-editor',
    path: '/products/:productId',
    titleVi: 'Chi tiết sản phẩm',
    icon: '🍜',
    permission: 'product.view',
    group: 'catalog',
    element: ProductEditorPage,
    hidden: true,
  },
  {
    id: 'combos',
    path: '/combos',
    titleVi: 'Combo',
    icon: '🍱',
    permission: 'product.view',
    group: 'catalog',
    element: CombosPage,
  },
  {
    id: 'promotions',
    path: '/promotions',
    titleVi: 'Khuyến mãi',
    icon: '🎁',
    permission: 'promotion.view',
    group: 'catalog',
    element: PromotionsPage,
  },
  {
    id: 'translations',
    path: '/translations',
    titleVi: 'Bản dịch',
    icon: '🌐',
    permission: 'translation.view',
    group: 'catalog',
    element: TranslationsPage,
    descriptionVi: 'Hàng đợi soát dịch, từ điển, nhà cung cấp AI và ngôn ngữ.',
  },

  // ---- people ------------------------------------------------------------
  {
    id: 'employees',
    path: '/employees',
    titleVi: 'Nhân viên',
    icon: '👥',
    permission: 'employee.view',
    group: 'people',
    element: EmployeesPage,
  },
  {
    id: 'roles',
    path: '/roles',
    titleVi: 'Vai trò & quyền',
    icon: '🔐',
    permission: 'employee.view',
    group: 'people',
    element: RolesPage,
    descriptionVi: 'Ma trận phân quyền theo vai trò.',
  },

  // ---- finance -----------------------------------------------------------
  {
    id: 'payments',
    path: '/payments',
    titleVi: 'Thanh toán',
    icon: '💳',
    permission: 'payment.view',
    group: 'finance',
    element: PaymentsPage,
  },
  {
    id: 'cash',
    path: '/cash',
    titleVi: 'Quỹ tiền mặt',
    icon: '💰',
    permission: 'cash.session.open',
    group: 'finance',
    element: CashPage,
  },

  // ---- system ------------------------------------------------------------
  {
    id: 'devices',
    path: '/devices',
    titleVi: 'Thiết bị & máy in',
    icon: '🖨️',
    permission: 'device.view',
    group: 'system',
    element: DevicesPage,
  },
  {
    id: 'audit',
    path: '/audit',
    titleVi: 'Nhật ký thao tác',
    icon: '📜',
    permission: 'audit.view',
    group: 'system',
    element: AuditLogPage,
  },
  {
    id: 'settings',
    path: '/settings',
    titleVi: 'Cấu hình',
    icon: '⚙️',
    permission: 'settings.view',
    group: 'system',
    element: SettingsPage,
  },
  {
    id: 'profile',
    path: '/profile',
    titleVi: 'Tài khoản của tôi',
    icon: '👤',
    // Everyone who can sign in can see their own profile and change their
    // password. There is no permission to gate that behind.
    permission: null,
    group: 'system',
    element: ProfilePage,
    hidden: true,
  },
];

/** Menu items for a given permission set, in registry order. */
export function visibleNavEntries(has: (permission: string) => boolean): readonly NavEntry[] {
  return NAV_ENTRIES.filter(
    (entry) => entry.hidden !== true && (entry.permission === null || has(entry.permission)),
  );
}

/** The first screen a user may actually open — used for the post-login redirect. */
export function landingPathFor(has: (permission: string) => boolean): string {
  const first = visibleNavEntries(has)[0];
  return first?.path ?? '/profile';
}
